/**
 * Wildlife behaviour: idle wandering + fleeing.
 *
 * Animals drift between random nearby points. When recently attacked they FLEE
 * directly away from whoever hit them for a few seconds (you have to chase them
 * down). Boars are aggressive — they don't flee; instead combat.ts gives them an
 * attack intent on their attacker, and while that intent is active this system
 * leaves them alone (combat drives the charge).
 */
import type { World } from "../world";
import type { Registry } from "../../data/registry";
import { FIXED_DT } from "../types";
import { findPath } from "../pathfinding";

export function faunaSystem(world: World, _reg: Registry): void {
  for (const [eid, f] of world.fauna) {
    const tr = world.transform.get(eid);
    const mv = world.move.get(eid);
    if (!tr || !mv) continue;

    // a provoked boar is charging via its attack intent — don't override it
    if (mv.intent) continue;

    if (f.fleeFor > 0) {
      f.fleeFor = Math.max(0, f.fleeFor - FIXED_DT);
      if (mv.path.length === 0) {
        // bolt directly away from the threat
        let dx = tr.pos.x - f.fleeFromX, dz = tr.pos.y - f.fleeFromZ;
        const len = Math.hypot(dx, dz) || 1;
        dx /= len; dz /= len;
        const run = world.size * (4 + world.rng.next() * 2);
        const tx = tr.pos.x + dx * run, ty = tr.pos.y + dz * run;
        mv.path = findPath(world, tr.pos.x, tr.pos.y, tx, ty, 6000, "land", undefined, false, tr.radius) ?? [];
      }
      continue;
    }

    // idle wander: occasionally amble to a random nearby spot
    f.wanderCd -= FIXED_DT;
    if (f.wanderCd <= 0 && mv.path.length === 0) {
      f.wanderCd = 3 + world.rng.next() * 5;
      const ang = world.rng.next() * Math.PI * 2;
      const dist = world.size * (2 + world.rng.next() * 3);
      const tx = tr.pos.x + Math.cos(ang) * dist, ty = tr.pos.y + Math.sin(ang) * dist;
      mv.path = findPath(world, tr.pos.x, tr.pos.y, tx, ty, 6000, "land", undefined, false, tr.radius) ?? [];
    }
  }
}
